import { createContext, useContext, useEffect, useMemo, useReducer, useCallback, useRef, type ReactNode, type Dispatch } from "react";
import type { Thought } from "./ThoughtCard";
import type { Collection } from "./CollectionCard";
import { supabase } from "../lib/supabaseClient";
import { track, type Surface } from "../lib/analytics";

/** How a card made in the web composer was entered; stored as thoughts.capture_method. */
export type WebCaptureMethod = "typed" | "dictated";
import { useAuth } from "../lib/auth/AuthProvider";
import { randomId } from "../lib/randomId";
import { promoteCollectionCoverToPublic } from "../lib/coverImages";
import {
  THOUGHT_COLUMNS,
  PAGE_SIZE,
  SEARCH_LIMIT,
  cursorFrom,
  isOptimisticId,
  keysetFilter,
  textSearchFilter,
  labelsMatching,
  byNewest,
  dedupeById,
  countConnections,
  rowToThought,
  distinctLabels,
  pageAll,
  searchLoadedThoughts,
  type ThoughtRow,
  type ConnectionRow,
  type PageResult,
  type ThoughtCursor,
  type ThoughtSearchResult,
} from "../lib/thoughtsQuery";

// ─── Extended types ─────────────────────────────────────────────
export interface CollectionFull extends Collection {
  card_ids: string[];
  created_at: string;
}

export type MapSnapshot = {
  cards: { thought_id: string; x: number; y: number }[];
  connections: { from_id: string; to_id: string; note?: string }[];
};

export interface Edition {
  id: string;
  db_id?: string;
  collection_id: string;
  name: string;
  created_at: string;
  thought_ids: string[];
  public_slug?: string;
  status?: "draft" | "published" | "unpublished";
  view_modes?: ("reader" | "map")[];
  map_snapshot?: MapSnapshot | null;
  cover_image_url?: string;
  view_count?: number;
}

export interface Thread {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  /** Private cover image (stored in the thought-images bucket, read via signed URL). */
  cover_image_path?: string;
}

/** Optional action button shown inside a toast (e.g. "Undo"). */
export interface ToastAction {
  label: string;
  onAction: () => void;
}

/** Auto-dismiss timings. Actionable toasts linger so there's time to react;
 *  RitualToast animates a countdown bar over TOAST_ACTION_MS. */
export const TOAST_MS = 2500;
export const TOAST_ACTION_MS = 6000;

/** Cards fetched on first paint. Small enough to keep hydrate fast; the rest
 *  arrives via the Library's infinite scroll or `loadAllThoughts()`. */
const HYDRATE_PAGE = 200;

/**
 * Fetch every row a hydrate query matches, in the `{ data, error }` shape the
 * rest of the batch already speaks.
 *
 * An unranged PostgREST select silently stops at the server's max-rows cap, and
 * a truncated hydrate is invisible: lists just end early and counts derived from
 * them read low, with nothing to distinguish that from genuinely owning less.
 * Paging costs an extra round-trip only when a page comes back full, so small
 * libraries pay nothing.
 *
 * Every caller must sort by something unique. Offset paging re-runs the query
 * per page, so rows tied under the sort can swap places between pages — which
 * both duplicates and skips. The trailing key in each `.order()` chain below is
 * that tiebreaker.
 */
/** Hydrate row shapes — the columns each `.select()` below actually asks for,
 *  in the nullability Postgres returns them. Mirrors `ThoughtRow`. */
interface CollectionRow {
  id: string;
  name: string;
  description: string | null;
  cover_color: string | null;
  cover_image_path: string | null;
  cover_image_public_url: string | null;
  is_public: boolean;
  public_slug: string | null;
  view_count: number | null;
  updated_at: string;
  created_at: string;
}

interface MembershipRow {
  collection_id: string;
  thought_id: string;
  position: number;
  added_at: string;
}

interface EditionRow {
  id: string;
  collection_id: string;
  name: string;
  created_at: string;
  public_slug: string | null;
  status: Edition["status"] | null;
  view_modes: ("reader" | "map")[] | null;
  map_snapshot: MapSnapshot | null;
  cover_image_url: string | null;
  view_count: number | null;
}

interface ThreadRow {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  cover_image_path: string | null;
}

async function allRows<T>(
  fetchPage: (from: number, to: number) => PromiseLike<PageResult<T>>
): Promise<{ data: T[]; error: unknown }> {
  try {
    const { rows } = await pageAll<T>(fetchPage);
    return { data: rows, error: null };
  } catch (error) {
    return { data: [], error };
  }
}

/**
 * The message an edge function sent with a non-2xx reply.
 *
 * `functions.invoke` reports a failed call as a FunctionsHttpError whose
 * `context` is the raw Response — the JSON body, and with it the only
 * explanation of what went wrong, is not on the error itself. Reading it is
 * the difference between "Failed to publish edition." and "that link is
 * already taken".
 */
async function readFunctionError(error: unknown): Promise<string | null> {
  const response = (error as { context?: Response } | null)?.context;
  if (!response || typeof response.json !== "function") return null;
  try {
    const body = await response.json();
    const message = (body as { error?: unknown })?.error;
    return typeof message === "string" && message.trim() ? message : null;
  } catch {
    // Not JSON, or the body was already consumed — fall back to the generic
    // message rather than showing the reader a parse error.
    return null;
  }
}

// ─── State shape ────────────────────────────────────────────────
export interface BurrowState {
  thoughts: Thought[];
  collections: CollectionFull[];
  editions: Edition[];
  savedThreads: Thread[];
  toastMessage: string | null;
  toastAction: ToastAction | null;
  activeThread: string[];
  hydrated: boolean;
  thoughtsHasMore: boolean;
  thoughtsLoadingMore: boolean;
  /**
   * How many cards the user actually owns, server-side — NOT `thoughts.length`.
   * `thoughts` only ever holds the pages we've fetched (200 on hydrate, then
   * whatever the Library's infinite scroll has pulled in), so any headline
   * "you have N thoughts" number read off the array flat-lines at 200. This is
   * an exact count from Postgres, adjusted locally on add/delete.
   */
  thoughtsTotal: number;
  /**
   * True once `thoughts` provably holds the user's entire library.
   *
   * Views that reason over the whole corpus — the map's graph, Thinking Space's
   * resurfacing deck, any filter or non-default sort in the Library — are wrong
   * on a partial set in a way the user can't see: the answer looks complete.
   * They call `loadAllThoughts()` and wait on this flag rather than quietly
   * computing over page one.
   */
  thoughtsAllLoaded: boolean;
  /** A full-library walk is in flight; pages stream in as it goes. */
  thoughtsLoadingAll: boolean;
  /**
   * How far sequential paging has reached, in (created_at DESC, id DESC).
   *
   * Held explicitly rather than read off the tail of `thoughts`, because that
   * array is no longer purely sequential: `ensureThoughtsLoaded` splices in
   * individual older cards a collection or edition refers to. Resuming from the
   * tail would then jump the cursor past everything between the paging frontier
   * and that one old card, skipping it all — silently, and permanently for the
   * session.
   */
  thoughtsCursor: ThoughtCursor | null;
  /**
   * Total connection endpoints across the whole library.
   *
   * Home's "connections" stat sums each loaded card's degree, which caps with
   * the loaded set exactly like the card count did. This is the same sum taken
   * over every connection row at hydrate, so the headline is right from the
   * first paint; once the full set is in memory the live sum takes over and
   * stays reactive to edits.
   */
  connectionsTotal: number;
  /**
   * Every distinct label in the library, not just those on loaded cards.
   *
   * The Library's tag chips are built from this: derived from `thoughts` they
   * silently omit any tag that happens to live only on an unloaded card, so a
   * filter the user needs simply isn't offered.
   */
  allLabels: string[];
  allLabelsLoaded: boolean;
}

/** Hydrate replaces the whole card set, so anything derived from it resets. */
type HydratePayload = Omit<
  BurrowState,
  | "toastMessage"
  | "toastAction"
  | "activeThread"
  | "savedThreads"
  | "thoughtsAllLoaded"
  | "thoughtsLoadingAll"
  | "allLabels"
  | "allLabelsLoaded"
> & { savedThreads?: Thread[]; thoughtsAllLoaded?: boolean };

// ─── Actions ────────────────────────────────────────────────────
export type Action =
  | { type: "SET_ALL"; state: HydratePayload }
  | { type: "ADD_THOUGHT"; thought: Thought }
  | { type: "APPEND_THOUGHTS"; thoughts: Thought[]; hasMore: boolean; cursor?: ThoughtCursor }
  /** Splice in specific cards fetched by id, without disturbing paging state. */
  | { type: "MERGE_THOUGHTS"; thoughts: Thought[] }
  | { type: "SET_THOUGHTS_LOADING_MORE"; loading: boolean }
  | { type: "SET_THOUGHTS_LOADING_ALL"; loading: boolean }
  | { type: "SET_THOUGHTS_ALL_LOADED" }
  | { type: "SET_ALL_LABELS"; labels: string[] }
  | { type: "UPDATE_THOUGHT"; id: string; updates: Partial<Thought> }
  | { type: "DELETE_THOUGHT"; id: string }
  | { type: "UPDATE_EDITION"; id: string; updates: Partial<Edition> }
  | { type: "ADD_TAG"; thoughtId: string; tag: string }
  | { type: "REMOVE_TAG"; thoughtId: string; tag: string }
  | { type: "ADD_TO_COLLECTION"; thoughtId: string; collectionId: string }
  | { type: "REMOVE_FROM_COLLECTION"; thoughtId: string; collectionId: string }
  | { type: "CREATE_COLLECTION"; collection: CollectionFull }
  | { type: "DELETE_COLLECTION"; id: string }
  | { type: "UPDATE_COLLECTION"; id: string; updates: Partial<CollectionFull> }
  | { type: "BULK_ADD_TO_COLLECTION"; thoughtIds: string[]; collectionId: string }
  | { type: "CREATE_EDITION"; edition: Edition }
  | { type: "DELETE_EDITION"; id: string }
  | { type: "SET_TOAST"; message: string | null; action?: ToastAction | null }
  | { type: "REORDER_COLLECTION"; collectionId: string; cardIds: string[] }
  | { type: "SET_ACTIVE_THREAD"; thread: string[] }
  | { type: "SET_SAVED_THREADS"; threads: Thread[] };

// ─── Reducer ────────────────────────────────────────────────────
export function burrowReducer(state: BurrowState, action: Action): BurrowState {
  switch (action.type) {
    case "SET_ALL":
      return {
        ...state,
        thoughts: action.state.thoughts,
        collections: action.state.collections,
        editions: action.state.editions,
        savedThreads: action.state.savedThreads ?? state.savedThreads,
        hydrated: action.state.hydrated,
        thoughtsHasMore: action.state.thoughtsHasMore,
        thoughtsLoadingMore: action.state.thoughtsLoadingMore,
        thoughtsTotal: action.state.thoughtsTotal,
        // A fresh card set invalidates everything derived from the old one.
        thoughtsCursor: action.state.thoughtsCursor,
        connectionsTotal: action.state.connectionsTotal,
        thoughtsAllLoaded: action.state.thoughtsAllLoaded ?? false,
        thoughtsLoadingAll: false,
        allLabels: [],
        allLabelsLoaded: false,
      };
    case "ADD_THOUGHT": {
      // Guard against double-adds (e.g. a borrow synced here after hydration
      // already picked the row up) — they would inflate `thoughtsTotal`.
      if (state.thoughts.some((t) => t.id === action.thought.id)) return state;
      return {
        ...state,
        thoughts: [action.thought, ...state.thoughts],
        thoughtsTotal: state.thoughtsTotal + 1,
      };
    }
    case "APPEND_THOUGHTS": {
      const existing = new Set(state.thoughts.map((t) => t.id));
      const merged = [...state.thoughts, ...action.thoughts.filter((t) => !existing.has(t.id))];
      return {
        ...state,
        thoughts: merged,
        thoughtsHasMore: action.hasMore,
        thoughtsLoadingMore: false,
        // Only the paging path advances the frontier.
        thoughtsCursor: action.cursor ?? state.thoughtsCursor,
        // Reaching the end by any route means the set is complete — the Library's
        // infinite scroll gets there as legitimately as loadAllThoughts does.
        thoughtsAllLoaded: state.thoughtsAllLoaded || !action.hasMore,
      };
    }
    case "MERGE_THOUGHTS": {
      const existing = new Set(state.thoughts.map((t) => t.id));
      const added = action.thoughts.filter((t) => !existing.has(t.id));
      if (added.length === 0) return state;
      // Sorted into place so the array keeps one canonical newest-first order;
      // `thoughtsCursor` is deliberately untouched, so paging still resumes from
      // the sequential frontier rather than from whichever old card landed last.
      return { ...state, thoughts: [...state.thoughts, ...added].sort(byNewest) };
    }
    case "SET_THOUGHTS_LOADING_MORE":
      return { ...state, thoughtsLoadingMore: action.loading };
    case "SET_THOUGHTS_LOADING_ALL":
      return { ...state, thoughtsLoadingAll: action.loading };
    case "SET_THOUGHTS_ALL_LOADED":
      return { ...state, thoughtsAllLoaded: true, thoughtsHasMore: false, thoughtsLoadingAll: false };
    case "SET_ALL_LABELS":
      return { ...state, allLabels: action.labels, allLabelsLoaded: true };

    case "UPDATE_THOUGHT":
      return {
        ...state,
        // If we're updating the thought `id` (optimistic temp -> real id),
        // keep any UI references (activeThread, collection card_ids) in sync.
        activeThread: action.updates.id
          ? state.activeThread.map((tid) => (tid === action.id ? action.updates.id as string : tid))
          : state.activeThread,
        collections: action.updates.id
          ? state.collections.map((c) => ({
              ...c,
              card_ids: c.card_ids.map((cid) => (cid === action.id ? (action.updates.id as string) : cid)),
            }))
          : state.collections,
        thoughts: state.thoughts.map((t) => (t.id === action.id ? { ...t, ...action.updates } : t)),
      };

    case "DELETE_THOUGHT":
      return {
        ...state,
        thoughts: state.thoughts.filter((t) => t.id !== action.id),
        thoughtsTotal: state.thoughts.some((t) => t.id === action.id)
          ? Math.max(0, state.thoughtsTotal - 1)
          : state.thoughtsTotal,
        activeThread: state.activeThread.filter((tid) => tid !== action.id),
        collections: state.collections.map((c) => ({
          ...c,
          card_ids: c.card_ids.filter((id) => id !== action.id),
          card_count: c.card_ids.filter((id) => id !== action.id).length,
        })),
      };

    case "UPDATE_EDITION":
      return {
        ...state,
        editions: state.editions.map((e) =>
          e.id === action.id ? { ...e, ...action.updates } : e
        ),
      };

    case "ADD_TAG": {
      // Fold a brand-new tag into the vocabulary too, so it shows up as a
      // Library filter chip immediately instead of only after the next reload.
      const known = state.allLabels.some((l) => l.toLowerCase() === action.tag.toLowerCase());
      return {
        ...state,
        allLabels: known ? state.allLabels : [...state.allLabels, action.tag].sort((a, b) => a.localeCompare(b)),
        thoughts: state.thoughts.map((t) =>
          t.id === action.thoughtId && !t.labels.includes(action.tag)
            ? { ...t, labels: [...t.labels, action.tag] }
            : t
        ),
      };
    }

    case "REMOVE_TAG":
      return {
        ...state,
        thoughts: state.thoughts.map((t) =>
          t.id === action.thoughtId
            ? { ...t, labels: t.labels.filter((tag) => tag !== action.tag) }
            : t
        ),
      };

    case "ADD_TO_COLLECTION": {
      return {
        ...state,
        collections: state.collections.map((c) =>
          c.id === action.collectionId && !c.card_ids.includes(action.thoughtId)
            ? {
                ...c,
                card_ids: [...c.card_ids, action.thoughtId],
                card_count: c.card_ids.length + 1,
                updated_at: new Date().toISOString(),
              }
            : c
        ),
      };
    }

    case "REMOVE_FROM_COLLECTION":
      return {
        ...state,
        collections: state.collections.map((c) =>
          c.id === action.collectionId
            ? {
                ...c,
                card_ids: c.card_ids.filter((id) => id !== action.thoughtId),
                card_count: c.card_ids.filter((id) => id !== action.thoughtId).length,
                updated_at: new Date().toISOString(),
              }
            : c
        ),
      };

    case "CREATE_COLLECTION":
      return { ...state, collections: [action.collection, ...state.collections] };

    case "DELETE_COLLECTION":
      return { ...state, collections: state.collections.filter((c) => c.id !== action.id) };

    case "UPDATE_COLLECTION":
      return {
        ...state,
        collections: state.collections.map((c) =>
          c.id === action.id ? { ...c, ...action.updates } : c
        ),
      };

    case "BULK_ADD_TO_COLLECTION":
      return {
        ...state,
        collections: state.collections.map((c) => {
          if (c.id !== action.collectionId) return c;
          const newIds = action.thoughtIds.filter((id) => !c.card_ids.includes(id));
          return {
            ...c,
            card_ids: [...c.card_ids, ...newIds],
            card_count: c.card_ids.length + newIds.length,
            updated_at: new Date().toISOString(),
          };
        }),
      };

    case "CREATE_EDITION":
      return { ...state, editions: [action.edition, ...state.editions] };

    case "DELETE_EDITION":
      return { ...state, editions: state.editions.filter((e) => e.id !== action.id) };

    case "REORDER_COLLECTION":
      return {
        ...state,
        collections: state.collections.map((c) =>
          c.id === action.collectionId ? { ...c, card_ids: action.cardIds } : c
        ),
      };

    case "SET_TOAST":
      return { ...state, toastMessage: action.message, toastAction: action.action ?? null };

    case "SET_ACTIVE_THREAD":
      return { ...state, activeThread: action.thread };

    case "SET_SAVED_THREADS":
      return { ...state, savedThreads: action.threads };

    default:
      return state;
  }
}

// ─── Context ────────────────────────────────────────────────────
/** The subset of a freshly-inserted borrowed card the store needs. Mirrors
 *  `BorrowResult["newCard"]` from socialApi. */
export interface BorrowedCardSeed {
  id: string;
  title: string | null;
  body: string;
  labels: string[];
  source_url: string | null;
  source_title: string | null;
  source_card_id: string | null;
  source_edition_id: string | null;
  created_at: string;
}

interface BurrowContextValue {
  state: BurrowState;
  dispatch: React.Dispatch<Action>;
  // Convenience helpers
  addThought: (body: string, title?: string, sourceUrl?: string, sourceTitle?: string, source?: Surface, collectionId?: string, captureMethod?: WebCaptureMethod) => Thought;
  addImageThought: (file: File, opts?: { body?: string; title?: string; sourceUrl?: string; sourceTitle?: string; source?: Surface }) => Thought;
  updateThought: (id: string, updates: Partial<Thought>) => void;
  deleteThought: (id: string) => void;
  /** Remove a public card from the commons entirely: the public row is deleted
   * and its content returns as a fresh private, attributed card. Returns the
   * new card id, or null on failure. `silent` suppresses the toasts (used when
   * withdraw runs as part of another flow, e.g. undoing an add). */
  withdrawFromCommons: (id: string, opts?: { silent?: boolean }) => Promise<string | null>;
  loadMoreThoughts: (opts?: { limit?: number }) => Promise<void>;
  /** Page in the rest of the library. Safe to call repeatedly — it no-ops once
   *  the set is complete or another paging operation is already running. */
  loadAllThoughts: () => Promise<void>;
  /** Populate `state.allLabels` with every tag in the library, loaded or not. */
  loadAllLabels: () => Promise<void>;
  /** Fetch any of these cards that aren't in memory yet, for views that resolve
   *  stored card ids (collections, editions, trails). */
  ensureThoughtsLoaded: (ids: string[]) => Promise<void>;
  /** Search every card server-side, not just the pages held in memory. */
  searchAllThoughts: (query: string) => Promise<ThoughtSearchResult>;
  addTag: (thoughtId: string, tag: string) => void;
  removeTag: (thoughtId: string, tag: string) => void;
  addToCollection: (thoughtId: string, collectionId: string) => void;
  removeFromCollection: (thoughtId: string, collectionId: string) => void;
  createCollection: (name: string, description?: string, isPublic?: boolean, coverColor?: string) => Promise<CollectionFull>;
  updateCollection: (id: string, updates: Partial<CollectionFull>) => void;
  // Publish/unpublish a collection as a *live* public page. Unlike editions
  // (frozen snapshots), a public collection always reflects current membership.
  // Returns its stable public_slug (null only if the call failed).
  setCollectionPublic: (id: string, isPublic: boolean) => Promise<string | null>;
  deleteCollection: (id: string) => void;
  bulkAddToCollection: (thoughtIds: string[], collectionId: string) => void;
  createEdition: (collectionId: string, name: string, customSlug?: string, password?: string, viewModes?: ("reader" | "map")[], mapSnapshot?: MapSnapshot | null, coverImageUrl?: string) => Promise<Edition>;
  reorderCollection: (collectionId: string, cardIds: string[]) => void;
  saveThreadDraft: (threadId: string | null, title: string, thoughtIds: string[]) => Promise<string>;
  setThreadCover: (threadId: string, path: string | undefined) => void;
  deleteThread: (threadId: string) => Promise<void>;
  publishThreadEdition: (
    threadId: string,
    name?: string
  ) => Promise<{ public_slug: string; edition_id: string }>;
  showToast: (message: string, action?: ToastAction) => void;
  /** Put a just-borrowed card into local state. The DB row already exists — this
   *  only stops the library/sidebar from lagging until the next full hydration. */
  registerBorrowedThought: (card: BorrowedCardSeed) => void;
  getCollectionThoughts: (collectionId: string) => Thought[];
  getThoughtCollections: (thoughtId: string) => CollectionFull[];
  searchThoughts: (query: string) => Thought[];
  setActiveThread: (thread: string[]) => void;
}

const BurrowContext = createContext<BurrowContextValue | null>(null);

let didWarnAboutMissingBurrowProvider = false;

const missingProviderError = new Error("useBurrow() was called without BurrowProvider");

export function useBurrow(): BurrowContextValue {
  const ctx = useContext(BurrowContext);
  if (ctx) return ctx;

  // Production-safety: avoid hard-crashing the entire app if the Provider is missing
  // (e.g., transient render during route transitions or hot reload).
  if (!didWarnAboutMissingBurrowProvider) {
    didWarnAboutMissingBurrowProvider = true;
    console.error(missingProviderError);
  }

  const fallbackThought: Thought = {
    id: "missing-provider",
    body: "",
    title: undefined,
    source_url: undefined,
    source_title: undefined,
    connections: 0,
    labels: [],
    created_at: new Date(0).toISOString(),
    author: "You",
    image_url: undefined,
  };

  const fallbackCollection: CollectionFull = {
    id: "missing-provider",
    name: "Missing provider",
    description: undefined,
    card_count: 0,
    is_public: false,
    cover_color: undefined,
    updated_at: new Date(0).toISOString(),
    created_at: new Date(0).toISOString(),
    card_ids: [],
  };

  const fallbackEdition: Edition = {
    id: "missing-provider",
    collection_id: "missing-provider",
    name: "Missing provider",
    created_at: new Date(0).toISOString(),
    thought_ids: [],
    status: "draft",
    public_slug: undefined,
    db_id: undefined,
  };

  const noopDispatch = (() => {}) as Dispatch<Action>;

  return {
    state: {
      thoughts: [],
      collections: [],
      editions: [],
      savedThreads: [],
      toastMessage: null,
      toastAction: null,
      activeThread: [],
      hydrated: false,
      thoughtsHasMore: false,
      thoughtsLoadingMore: false,
      thoughtsTotal: 0,
      thoughtsAllLoaded: false,
      thoughtsLoadingAll: false,
      thoughtsCursor: null,
      connectionsTotal: 0,
      allLabels: [],
      allLabelsLoaded: false,
    },
    dispatch: noopDispatch,

    // Convenience helpers (no-ops / empty results)
    addThought: (_body: string, _title?: string, _sourceUrl?: string, _sourceTitle?: string, _source?: Surface, _collectionId?: string, _captureMethod?: WebCaptureMethod) => fallbackThought,
    addImageThought: (
      _file: File,
      _opts?: { body?: string; title?: string; sourceUrl?: string; sourceTitle?: string; source?: Surface }
    ) => fallbackThought,
    updateThought: (_id: string, _updates: Partial<Thought>) => {},
    deleteThought: (_id: string) => {},
    withdrawFromCommons: async (_id: string, _opts?: { silent?: boolean }) => null,
    loadMoreThoughts: async (_opts?: { limit?: number }) => {},
    loadAllThoughts: async () => {},
    loadAllLabels: async () => {},
    ensureThoughtsLoaded: async (_ids: string[]) => {},
    searchAllThoughts: async (_query: string) => ({ results: [], truncated: false, complete: true }),
    addTag: (_thoughtId: string, _tag: string) => {},
    removeTag: (_thoughtId: string, _tag: string) => {},
    addToCollection: (_thoughtId: string, _collectionId: string) => {},
    removeFromCollection: (_thoughtId: string, _collectionId: string) => {},
    createCollection: async (name: string, description?: string, isPublic?: boolean, coverColor?: string) => ({
      ...fallbackCollection,
      name,
      description,
      is_public: Boolean(isPublic),
      cover_color: coverColor ?? fallbackCollection.cover_color,
    }),
    updateCollection: (_id: string, _updates: Partial<CollectionFull>) => {},
    setCollectionPublic: async (_id: string, _isPublic: boolean) => null,
    deleteCollection: (_id: string) => {},
    bulkAddToCollection: (_thoughtIds: string[], _collectionId: string) => {},
    createEdition: async (collectionId: string, name: string) => ({ ...fallbackEdition, collection_id: collectionId, name }),
    reorderCollection: (_collectionId: string, _cardIds: string[]) => {},
    deleteThread: async () => {},
    setThreadCover: (_threadId: string, _path: string | undefined) => {},
    saveThreadDraft: async (_threadId: string | null, _title: string, _thoughtIds: string[]) => {
      throw missingProviderError;
    },
    publishThreadEdition: async (_threadId: string, _name?: string) => {
      throw missingProviderError;
    },
    showToast: (_message: string, _action?: ToastAction) => {},
    registerBorrowedThought: (_card: BorrowedCardSeed) => {},
    getCollectionThoughts: (_collectionId: string) => [],
    getThoughtCollections: (_thoughtId: string) => [],
    searchThoughts: (_query: string) => [],
    setActiveThread: (_thread: string[]) => {},
  };
}

// ─── Provider ───────────────────────────────────────────────────
export function BurrowProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [state, dispatch] = useReducer(burrowReducer, {
    thoughts: [],
    collections: [],
    editions: [],
    savedThreads: [],
    toastMessage: null,
    toastAction: null,
    activeThread: [],
    hydrated: false,
    thoughtsHasMore: true,
    thoughtsLoadingMore: false,
    thoughtsTotal: 0,
    thoughtsAllLoaded: false,
    thoughtsLoadingAll: false,
    thoughtsCursor: null,
    connectionsTotal: 0,
    allLabels: [],
    allLabelsLoaded: false,
  });

  // Always-current state for the async paging/search helpers to read.
  //
  // Closing over `state` instead would force every one of those callbacks to
  // list it as a dependency, so their identity would change on every dispatch —
  // and the Library's IntersectionObserver, which is torn down and rebuilt
  // whenever `loadMoreThoughts` changes, would be re-created on every keystroke.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    const hydrate = async () => {
      // Wait for the session to resolve. While auth is still "loading" we must
      // NOT mark the store hydrated — doing so advertises "loaded & empty" for
      // the split second before the session lands, which flashes every page
      // into a false not-found / empty state. Leave hydrated=false so pages
      // keep showing loaders/skeletons until we actually know the auth state.
      if (auth.status === "loading") return;

      if (auth.status === "signed_out") {
        dispatch({
          type: "SET_ALL",
          state: {
            thoughts: [],
            collections: [],
            editions: [],
            hydrated: true,
            thoughtsHasMore: false,
            thoughtsLoadingMore: false,
            thoughtsTotal: 0,
            thoughtsCursor: null,
            connectionsTotal: 0,
          },
        });
        return;
      }

      const userId = auth.user.id;

      const results = await Promise.all([
          // `count: "exact"` rides along on the same round trip and reports how
          // many cards match the filter in total, not how many this page
          // returned — that's what every "you have N thoughts" stat needs.
          // `id` is the sort tiebreaker so keyset paging in loadMoreThoughts
          // can't skip cards that share a created_at.
          supabase
            .from("thoughts")
            .select(THOUGHT_COLUMNS, { count: "exact" })
            .eq("owner_id", userId)
            .order("created_at", { ascending: false })
            .order("id", { ascending: false })
            .range(0, HYDRATE_PAGE - 1),
          allRows<CollectionRow>((from, to) =>
            supabase
              .from("collections")
              .select("id,name,description,cover_color,cover_image_path,cover_image_public_url,is_public,public_slug,view_count,updated_at,created_at")
              .eq("owner_id", userId)
              .order("updated_at", { ascending: false })
              .order("id", { ascending: false })
              .range(from, to)
          ),
          // Sorted by position first so each collection's slice comes back in
          // reading order; grouping a globally sorted list preserves it.
          allRows<MembershipRow>((from, to) =>
            supabase
              .from("collection_thoughts")
              .select("collection_id,thought_id,position,added_at")
              .eq("owner_id", userId)
              .order("position", { ascending: true })
              .order("added_at", { ascending: true })
              .order("collection_id", { ascending: true })
              .order("thought_id", { ascending: true })
              .range(from, to)
          ),
          // Capping this one made every card past the cap read as having zero
          // connections, which is indistinguishable from genuinely having none.
          allRows<{ from_thought_id: string; to_thought_id: string }>((from, to) =>
            supabase
              .from("connections")
              .select("from_thought_id,to_thought_id")
              .eq("owner_id", userId)
              .order("id", { ascending: true })
              .range(from, to)
          ),
          allRows<EditionRow>((from, to) =>
            supabase
              .from("editions")
              .select("id,collection_id,name,created_at,public_slug,status,view_modes,map_snapshot,cover_image_url,view_count")
              .eq("owner_id", userId)
              .order("created_at", { ascending: false })
              .order("id", { ascending: false })
              .range(from, to)
          ),
          allRows<ThreadRow>((from, to) =>
            supabase
              .from("threads")
              .select("id,title,created_at,updated_at,cover_image_path")
              .eq("owner_id", userId)
              .order("updated_at", { ascending: false })
              .order("id", { ascending: false })
              .range(from, to)
          ),
        ]);

      results.forEach((r, i) => {
        if (r.error) console.error(`Hydration query ${i} failed:`, r.error);
      });

      const [{ data: thoughts, count: thoughtsCount }, { data: collections }, { data: memberships }, { data: connections }, { data: editions }, { data: threads }] = results;

      // edition_thoughts + thread_thoughts both depend only on the batch above
      // and not on each other — run them together so hydrate costs one extra
      // round-trip, not two.
      const editionIds = (editions ?? []).map((e) => e.id);
      const threadIds = (threads ?? []).map((t) => t.id);
      const [{ data: editionThoughts }, { data: threadThoughtRows }] = await Promise.all([
        editionIds.length > 0
          ? allRows<{ edition_id: string; thought_id: string; position: number }>((from, to) =>
              supabase
                .from("edition_thoughts")
                .select("edition_id,thought_id,position")
                .in("edition_id", editionIds)
                .order("edition_id", { ascending: true })
                .order("position", { ascending: true })
                .order("thought_id", { ascending: true })
                .range(from, to)
            )
          : Promise.resolve({ data: [] as { edition_id: string; thought_id: string; position: number }[] }),
        threadIds.length > 0
          ? allRows<{ thought_id: string }>((from, to) =>
              supabase
                .from("thread_thoughts")
                .select("thought_id")
                .in("thread_id", threadIds)
                .order("thread_id", { ascending: true })
                .order("position", { ascending: true })
                .order("thought_id", { ascending: true })
                .range(from, to)
            )
          : Promise.resolve({ data: [] as { thought_id: string }[] }),
      ]);

      const threadThoughtIdSet = new Set<string>((threadThoughtRows ?? []).map((r: any) => r.thought_id));

      const connCounts = countConnections(connections ?? []);

      const uiThoughts: Thought[] = ((thoughts ?? []) as unknown as ThoughtRow[]).map((t) => ({
        ...rowToThought(t, connCounts.get(t.id) ?? 0),
        in_thread: threadThoughtIdSet.has(t.id),
      }));

      const cardIdsByCollection = new Map<string, string[]>();
      (memberships ?? []).forEach((m) => {
        const arr = cardIdsByCollection.get(m.collection_id) ?? [];
        arr.push(m.thought_id);
        cardIdsByCollection.set(m.collection_id, arr);
      });

      const uiCollections: CollectionFull[] = (collections ?? []).map((c) => {
        const card_ids = cardIdsByCollection.get(c.id) ?? [];
        return {
          id: c.id,
          name: c.name,
          description: c.description ?? undefined,
          card_count: card_ids.length,
          is_public: c.is_public,
          public_slug: c.public_slug ?? undefined,
          view_count: c.view_count ?? undefined,
          cover_color: c.cover_color ?? undefined,
          cover_image_path: c.cover_image_path ?? undefined,
          cover_image_public_url: c.cover_image_public_url ?? undefined,
          updated_at: c.updated_at,
          created_at: c.created_at,
          card_ids,
        };
      });

      const thoughtsByEdition = new Map<string, string[]>();
      (editionThoughts ?? [])
        .sort((a, b) => a.position - b.position)
        .forEach((et) => {
          const arr = thoughtsByEdition.get(et.edition_id) ?? [];
          arr.push(et.thought_id);
          thoughtsByEdition.set(et.edition_id, arr);
        });

      const uiEditions: Edition[] = (editions ?? []).map((e) => ({
        id: e.public_slug ?? e.id,
        db_id: e.id,
        collection_id: e.collection_id,
        name: e.name,
        created_at: e.created_at,
        thought_ids: thoughtsByEdition.get(e.id) ?? [],
        public_slug: e.public_slug ?? undefined,
        status: e.status ?? undefined,
        view_modes: e.view_modes ?? ["reader"],
        map_snapshot: e.map_snapshot ?? null,
        cover_image_url: e.cover_image_url ?? undefined,
        view_count: e.view_count ?? 0,
      }));

      const uiThreads: Thread[] = (threads ?? []).map((t) => ({
        id: t.id,
        title: t.title,
        created_at: t.created_at,
        updated_at: t.updated_at,
        cover_image_path: t.cover_image_path ?? undefined,
      }));

      dispatch({
        type: "SET_ALL",
        state: {
          thoughts: uiThoughts,
          collections: uiCollections,
          editions: uiEditions,
          savedThreads: uiThreads,
          hydrated: true,
          // Prefer the exact count when the server gave us one — it knows
          // there's a page 2 without waiting for a short page to prove it.
          thoughtsHasMore:
            typeof thoughtsCount === "number"
              ? thoughtsCount > uiThoughts.length
              : (thoughts ?? []).length >= HYDRATE_PAGE,
          thoughtsLoadingMore: false,
          thoughtsTotal: thoughtsCount ?? uiThoughts.length,
          // Where sequential paging resumes from. Taken from the rows as sorted
          // by the query, not from `uiThoughts`, so it stays the true frontier.
          thoughtsCursor: cursorFrom(uiThoughts) ?? null,
          connectionsTotal: Array.from(connCounts.values()).reduce((a, b) => a + b, 0),
          // A library that fits in the first page is already complete, so the
          // map and Thinking Space can skip their full-load round trip.
          thoughtsAllLoaded:
            typeof thoughtsCount === "number"
              ? thoughtsCount <= uiThoughts.length
              : (thoughts ?? []).length < HYDRATE_PAGE,
        },
      });
    };

    // Never leave the store un-hydrated on a thrown query — the whole authed
    // app now waits on `hydrated`, so a failed hydrate must still unblock it
    // (with empty data + genuine error/empty states) rather than hang a loader.
    hydrate().catch((err) => {
      console.error("Hydration failed:", err);
      dispatch({
        type: "SET_ALL",
        state: {
          thoughts: [],
          collections: [],
          editions: [],
          hydrated: true,
          thoughtsHasMore: false,
          thoughtsLoadingMore: false,
          thoughtsTotal: 0,
          thoughtsCursor: null,
          connectionsTotal: 0,
        },
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status]);

  // Single shared timer so a new toast cancels the previous auto-dismiss —
  // otherwise an earlier 2.5s timeout could clear a later toast (e.g. cut an
  // Undo window short). Toasts with an action get longer to act on.
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((message: string, action?: ToastAction) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    dispatch({ type: "SET_TOAST", message, action: action ?? null });
    toastTimer.current = setTimeout(() => {
      dispatch({ type: "SET_TOAST", message: null, action: null });
      toastTimer.current = null;
    }, action ? TOAST_ACTION_MS : TOAST_MS);
  }, []);

  const addThought = useCallback(
    (body: string, title?: string, sourceUrl?: string, sourceTitle?: string, source: Surface = "quick_capture", collectionId?: string, captureMethod: WebCaptureMethod = "typed"): Thought => {
      const tempId = `temp-${Date.now()}-${randomId()}`;
      const now = new Date().toISOString();
      // When a card is created straight into a collection, seed it with that
      // collection's name as a label — and persist both the label and the
      // collection membership once the real id lands. (addToCollection can't do
      // this here because it skips all DB writes for optimistic `temp-` ids, so
      // calling it with the temp id would drop the row and the label on reload.)
      const col = collectionId ? state.collections.find((c) => c.id === collectionId) : undefined;
      const initialLabels = col ? [col.name.trim().toLowerCase()] : [];
      const thought: Thought = {
        id: tempId,
        body,
        title,
        source_url: sourceUrl,
        source_title: sourceTitle,
        connections: 0,
        labels: initialLabels,
        created_at: now,
        author: "You",
      };
      dispatch({ type: "ADD_THOUGHT", thought });
      // Optimistically place it in the collection; the temp->real id swap in the
      // UPDATE_THOUGHT reducer keeps the collection's card_ids in sync.
      if (col) dispatch({ type: "ADD_TO_COLLECTION", thoughtId: tempId, collectionId: col.id });

      if (auth.status === "signed_in") {
        supabase
          .from("thoughts")
          .insert({
            owner_id: auth.user.id,
            title: title ?? null,
            body,
            source_url: sourceUrl ?? null,
            source_title: sourceTitle ?? null,
            labels: initialLabels,
            capture_method: captureMethod,
          })
          .select("id,created_at,updated_at")
          .single()
          .then(({ data, error }) => {
            if (error) {
              console.error("Failed to save thought:", error);
              showToast("Failed to save — try again.");
              return;
            }
            if (!data) return;
            dispatch({
              type: "UPDATE_THOUGHT",
              id: tempId,
              updates: { id: data.id, created_at: data.created_at, edited_at: data.updated_at },
            });
            // Now that we have the real id, persist collection membership.
            if (col) {
              supabase.from("collection_thoughts").upsert({
                owner_id: auth.user.id,
                collection_id: col.id,
                thought_id: data.id,
                position: col.card_ids.length,
              }).then(({ error }) => { if (error) showToast("Failed to add to collection."); });
            }
            track("card_created", {
              card_id: data.id,
              card_type: sourceUrl ? "link" : "text",
              surface: source,
              has_title: !!title,
              has_body: !!body.trim(),
              has_source: !!sourceUrl,
              char_count: body.length,
            });
          });
      }

      showToast(col ? `Added to "${col.name}".` : "Thought kept.");
      return thought;
    },
    [auth.status, auth.user, showToast, state.collections]
  );

  const addImageThought = useCallback(
    (file: File, opts?: { body?: string; title?: string; sourceUrl?: string; sourceTitle?: string; source?: Surface }): Thought => {
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
      if (!file.type.startsWith("image/") || !allowedTypes.includes(file.type)) {
        showToast("Only images are allowed (JPG, PNG, GIF, WebP).");
        return { id: "", body: "", connections: 0, labels: [], created_at: "", author: "" };
      }

      // Validate file size (max 5 MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        showToast("Image must be under 5 MB.");
        return { id: "", body: "", connections: 0, labels: [], created_at: "", author: "" };
      }

      // randomId() suffix keeps the optimistic id unique when several photos are
      // added in the same millisecond (bulk multi-photo capture).
      const tempId = `temp-${Date.now()}-${randomId()}`;
      const now = new Date().toISOString();
      const localUrl = URL.createObjectURL(file);

      const thought: Thought = {
        id: tempId,
        body: (opts?.body ?? "").trim(),
        title: opts?.title,
        source_url: opts?.sourceUrl,
        source_title: opts?.sourceTitle,
        connections: 0,
        labels: [],
        created_at: now,
        author: "You",
        image_url: localUrl,
      };

      dispatch({ type: "ADD_THOUGHT", thought });

      if (auth.status === "signed_in") {
        (async () => {
          const { data: inserted, error: insErr } = await supabase
            .from("thoughts")
            .insert({
              owner_id: auth.user.id,
              title: thought.title ?? null,
              body: thought.body,
              source_url: thought.source_url ?? null,
              source_title: thought.source_title ?? null,
              labels: [],
            })
            .select("id,created_at,updated_at")
            .single();

          if (insErr || !inserted) {
            if (insErr) console.error("Failed to save image thought:", insErr);
            showToast("Failed to save — try again.");
            return;
          }

          const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
          const safeExt = /^[a-z0-9]+$/.test(ext) ? ext : "jpg";
          const path = `${auth.user.id}/${inserted.id}/${randomId()}.${safeExt}`;

          const { error: upErr } = await supabase.storage
            .from("thought-images")
            .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });

          if (upErr) {
            console.error("Failed to upload image:", upErr);
            showToast("Image upload failed.");
            return;
          }

          await supabase.from("thoughts").update({ image_path: path }).eq("id", inserted.id);

          dispatch({
            type: "UPDATE_THOUGHT",
            id: tempId,
            updates: {
              id: inserted.id,
              created_at: inserted.created_at,
              edited_at: inserted.updated_at,
              image_path: path,
              image_url: undefined,
            },
          });
          track("card_created", {
            card_id: inserted.id,
            card_type: "image",
            surface: opts?.source ?? "quick_capture",
            has_title: !!thought.title,
            has_body: !!thought.body.trim(),
            has_source: !!thought.source_url,
            char_count: thought.body.length,
          });
        })();
      }

      showToast("Thought kept.");
      return thought;
    },
    [auth.status, auth.user, showToast]
  );

  const updateThought = useCallback((id: string, updates: Partial<Thought>) => {
    dispatch({ type: "UPDATE_THOUGHT", id, updates });
    if (auth.status === "signed_in" && !id.startsWith("temp-")) {
      // Build update payload — only include fields that were explicitly passed
      const payload: Record<string, unknown> = {};
      if ("title" in updates) payload.title = updates.title ?? null;
      if ("body" in updates) payload.body = updates.body;
      if ("source_url" in updates) payload.source_url = updates.source_url ?? null;
      if ("source_title" in updates) payload.source_title = updates.source_title ?? null;
      if ("labels" in updates) payload.labels = updates.labels;
      if ("image_path" in updates) payload.image_path = updates.image_path ?? null;
      if ("notes" in updates) payload.notes = updates.notes ?? null;
      // Editing the content (title/body) makes a borrowed card the owner's own work,
      // flipping attribution away from the original author. Labels/notes/image don't
      // count as content edits, so they don't change authorship.
      if ("title" in updates || "body" in updates) payload.edited = true;
      // Social v1: card visibility is DERIVED from edition membership in the DB
      // (trigger-managed). It is NOT user-settable from this endpoint. Any
      // visibility key passed in is silently ignored — the only way to make a
      // card public is to publish its collection as a public edition.

      if (Object.keys(payload).length === 0) return;

      supabase
        .from("thoughts")
        .update(payload)
        .eq("id", id)
        .eq("owner_id", auth.user.id)
        .then(({ error }) => { if (error) showToast("Failed to save changes."); });
    }
  }, [auth.status, auth.user, showToast]);

  // ── Card paging ───────────────────────────────────────────────
  //
  // Paging operations are serialised through a promise chain rather than being
  // dropped when one is already running. Dropping was the obvious approach and
  // it's wrong: applying a Library filter mid-scroll would fire
  // `loadAllThoughts` while a scroll page was still in flight, it would bail,
  // and nothing would ever retry — leaving the filter permanently answering
  // from a partial set. Queuing means the filter always gets its full library.
  //
  // The `pending` refs are what stop a queue from *building*: they flip
  // synchronously, so two IntersectionObserver fires in the same tick can't
  // both enqueue (`state.thoughtsLoadingMore` only becomes visible a render
  // later, far too late to prevent the duplicate).
  const chainRef = useRef<Promise<unknown>>(Promise.resolve());
  const morePendingRef = useRef(false);
  const allPendingRef = useRef(false);
  const labelsRef = useRef(false);

  /** Queue work behind any paging already in flight. */
  const enqueue = useCallback((task: () => Promise<void>): Promise<void> => {
    const next = chainRef.current.then(task, task);
    chainRef.current = next.catch(() => {});
    return next;
  }, []);

  /**
   * Undirected connection counts for a specific set of cards.
   *
   * Chunked because the ids travel in the query *string*: a thousand uuids
   * listed twice is a ~70 KB URL, which proxies reject long before Postgres
   * sees it.
   */
  const fetchConnectionCounts = useCallback(
    async (ownerId: string, ids: string[]): Promise<Map<string, number>> => {
      if (ids.length === 0) return new Map();
      const CHUNK = 150;
      const all: ConnectionRow[] = [];
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK).join(",");
        const { rows } = await pageAll<ConnectionRow>((from, to) =>
          supabase
            .from("connections")
            .select("from_thought_id,to_thought_id")
            .eq("owner_id", ownerId)
            .or(`from_thought_id.in.(${slice}),to_thought_id.in.(${slice})`)
            .range(from, to)
        );
        all.push(...rows);
      }
      return countConnections(all);
    },
    []
  );

  /** Every connection the user owns, in one walk. Cheaper than per-card lookups
   *  once the card count passes a couple of chunks. */
  const fetchAllConnectionCounts = useCallback(async (ownerId: string): Promise<Map<string, number>> => {
    const { rows } = await pageAll<ConnectionRow>((from, to) =>
      supabase
        .from("connections")
        .select("from_thought_id,to_thought_id")
        .eq("owner_id", ownerId)
        .range(from, to)
    );
    return countConnections(rows);
  }, []);

  /**
   * Which of these cards sit in one of the user's trails.
   *
   * Hydrate resolves this for its first page, but every later page skipped it,
   * leaving `in_thread` undefined — and undefined reads as "loose" in Thinking
   * Space, so paging the Library quietly filled the resurfacing deck with cards
   * that are already filed in a trail.
   *
   * Scoped by `owner_id` rather than by a list of thread ids: it's the same set
   * (RLS aside, a user's trail rows are exactly their trails') and it keeps the
   * whole membership reachable in one walk, without a URL full of ids or a
   * dependency on the trail list having hydrated first. `thoughtIds: null`
   * fetches the lot, for callers decorating many pages.
   */
  const fetchInThreadIds = useCallback(
    async (ownerId: string, thoughtIds: string[] | null): Promise<Set<string>> => {
      const out = new Set<string>();
      const walk = (ids: string[] | null) =>
        pageAll<{ thought_id: string }>((from, to) => {
          const q = supabase.from("thread_thoughts").select("thought_id").eq("owner_id", ownerId);
          return (ids ? q.in("thought_id", ids) : q).range(from, to);
        });

      if (thoughtIds === null) {
        const { rows } = await walk(null);
        rows.forEach((r) => out.add(r.thought_id));
        return out;
      }
      if (thoughtIds.length === 0) return out;

      const CHUNK = 150;
      for (let i = 0; i < thoughtIds.length; i += CHUNK) {
        const { rows } = await walk(thoughtIds.slice(i, i + CHUNK));
        rows.forEach((r) => out.add(r.thought_id));
      }
      return out;
    },
    []
  );

  /**
   * One keyset page of the user's cards.
   *
   * `decor` lets a caller that is walking the whole table resolve connections
   * and trail membership once up front instead of re-querying them per page —
   * two round trips for the whole walk rather than a dozen per page.
   */
  const fetchThoughtPage = useCallback(
    async (
      cursor: ThoughtCursor | undefined,
      limit: number,
      decor?: { connCounts: Map<string, number>; inThread: Set<string> }
    ): Promise<{ thoughts: Thought[]; rowCount: number; last?: ThoughtCursor }> => {
      if (auth.status !== "signed_in") return { thoughts: [], rowCount: 0 };
      const ownerId = auth.user.id;

      const base = supabase
        .from("thoughts")
        .select(THOUGHT_COLUMNS)
        .eq("owner_id", ownerId)
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(0, limit - 1);

      const { data, error } = cursor ? await base.or(keysetFilter(cursor)) : await base;
      if (error) throw error;

      const rows = (data ?? []) as unknown as ThoughtRow[];
      if (rows.length === 0) return { thoughts: [], rowCount: 0 };

      const ids = rows.map((r) => r.id);
      let connCounts: Map<string, number>;
      let inThread: Set<string>;
      if (decor) {
        ({ connCounts, inThread } = decor);
      } else {
        [connCounts, inThread] = await Promise.all([
          fetchConnectionCounts(ownerId, ids),
          fetchInThreadIds(ownerId, ids),
        ]);
      }

      return {
        thoughts: rows.map((r) => ({
          ...rowToThought(r, connCounts.get(r.id) ?? 0),
          in_thread: inThread.has(r.id),
        })),
        rowCount: rows.length,
        last: cursorFrom(rows),
      };
    },
    [auth.status, auth.user, fetchConnectionCounts, fetchInThreadIds]
  );

  const loadMoreThoughts = useCallback(
    async ({ limit = 100 }: { limit?: number } = {}) => {
      if (auth.status !== "signed_in") return;
      // A full-library walk supersedes incremental paging — let it finish
      // rather than interleaving a redundant page behind it.
      if (morePendingRef.current || allPendingRef.current) return;
      if (!stateRef.current.thoughtsHasMore) return;

      morePendingRef.current = true;
      dispatch({ type: "SET_THOUGHTS_LOADING_MORE", loading: true });

      await enqueue(async () => {
        try {
          const current = stateRef.current;
          if (!current.thoughtsHasMore) {
            dispatch({ type: "SET_THOUGHTS_LOADING_MORE", loading: false });
            return;
          }

          const { thoughts: page, rowCount, last } = await fetchThoughtPage(current.thoughtsCursor ?? undefined, limit);

          // A short page is the only trustworthy end-of-set signal under keyset
          // paging. `thoughtsTotal` can drift (another device adding cards, a
          // borrow landing mid-scroll), so it may only stop us *early* — never
          // keep us going, which would strand the tail or spin the observer.
          const loaded = new Set([...current.thoughts.map((t) => t.id), ...page.map((t) => t.id)]).size;
          const nextHasMore =
            rowCount >= limit && (current.thoughtsTotal <= 0 || loaded < current.thoughtsTotal);

          dispatch({ type: "APPEND_THOUGHTS", thoughts: page, hasMore: nextHasMore, cursor: last });
        } catch (err) {
          console.error("Loading more thoughts failed:", err);
          dispatch({ type: "SET_THOUGHTS_LOADING_MORE", loading: false });
        } finally {
          morePendingRef.current = false;
        }
      });
    },
    [auth.status, enqueue, fetchThoughtPage]
  );

  const loadAllThoughts = useCallback(async () => {
    if (auth.status !== "signed_in") return;
    if (allPendingRef.current || stateRef.current.thoughtsAllLoaded) return;

    allPendingRef.current = true;
    dispatch({ type: "SET_THOUGHTS_LOADING_ALL", loading: true });

    await enqueue(async () => {
      try {
        if (stateRef.current.thoughtsAllLoaded) {
          dispatch({ type: "SET_THOUGHTS_LOADING_ALL", loading: false });
          return;
        }

        const ownerId = auth.user.id;
        // Resolved once for the whole walk: the per-page form lists card ids in
        // the query string, which is both slower and length-limited.
        const [connCounts, inThread] = await Promise.all([
          fetchAllConnectionCounts(ownerId),
          fetchInThreadIds(ownerId, null),
        ]);

        // Resume from the sequential frontier, carrying the cursor forward from
        // each page's own last row — reading it back off `state` would deadlock,
        // since the dispatched pages never land in this closure.
        let cursor = stateRef.current.thoughtsCursor ?? undefined;
        for (;;) {
          const { thoughts: page, rowCount, last } = await fetchThoughtPage(cursor, PAGE_SIZE, {
            connCounts,
            inThread,
          });
          if (rowCount === 0) break;
          // Dispatched as they arrive, so the map and the resurfacing deck fill
          // in progressively instead of blocking on the whole walk.
          dispatch({ type: "APPEND_THOUGHTS", thoughts: page, hasMore: rowCount >= PAGE_SIZE, cursor: last });
          if (rowCount < PAGE_SIZE) break;
          cursor = last;
        }
        dispatch({ type: "SET_THOUGHTS_ALL_LOADED" });
      } catch (err) {
        console.error("Loading the full library failed:", err);
        // Clear the flag so the UI stops promising more is coming, and say so —
        // views that need the whole set would otherwise sit on a spinner.
        dispatch({ type: "SET_THOUGHTS_LOADING_ALL", loading: false });
        showToast("Couldn't load your whole library — showing what's loaded so far.");
      } finally {
        allPendingRef.current = false;
      }
    });
  }, [auth.status, auth.user, enqueue, fetchThoughtPage, fetchAllConnectionCounts, fetchInThreadIds, showToast]);

  /**
   * Make sure specific cards are in memory, fetching only the missing ones.
   *
   * Collections, editions and trails store card *ids* and resolve them against
   * `state.thoughts`. Any member older than the paging frontier simply wasn't
   * there, so it was dropped from the view — a collection would quietly render
   * fewer cards than it contains, with nothing to indicate the omission.
   *
   * Targeted rather than a full `loadAllThoughts`: opening a twenty-card
   * collection shouldn't drag the entire library across the wire.
   */
  const ensureThoughtsLoaded = useCallback(
    async (ids: string[]) => {
      if (auth.status !== "signed_in") return;
      const have = new Set(stateRef.current.thoughts.map((t) => t.id));
      const missing = Array.from(new Set(ids)).filter((id) => id && !have.has(id) && !isOptimisticId(id));
      if (missing.length === 0) return;

      const ownerId = auth.user.id;
      try {
        const CHUNK = 150;
        const rows: ThoughtRow[] = [];
        for (let i = 0; i < missing.length; i += CHUNK) {
          const { rows: page } = await pageAll<ThoughtRow>((from, to) =>
            supabase
              .from("thoughts")
              .select(THOUGHT_COLUMNS)
              .eq("owner_id", ownerId)
              .in("id", missing.slice(i, i + CHUNK))
              .range(from, to)
          );
          rows.push(...page);
        }
        if (rows.length === 0) return;

        const found = rows.map((r) => r.id);
        const [connCounts, inThread] = await Promise.all([
          fetchConnectionCounts(ownerId, found),
          fetchInThreadIds(ownerId, found),
        ]);

        dispatch({
          type: "MERGE_THOUGHTS",
          thoughts: rows.map((r) => ({
            ...rowToThought(r, connCounts.get(r.id) ?? 0),
            in_thread: inThread.has(r.id),
          })),
        });
      } catch (err) {
        console.error("Loading referenced cards failed:", err);
      }
    },
    [auth.status, auth.user, fetchConnectionCounts, fetchInThreadIds]
  );

  /**
   * The complete label vocabulary, independent of which cards are loaded.
   *
   * Only the `labels` column is selected, so this stays a small fraction of the
   * cost of `loadAllThoughts` even on a large library. (A `distinct
   * unnest(labels)` RPC would be cheaper still, but it would need a migration
   * deployed to every environment for a query that is already comfortably fast.)
   */
  const loadAllLabels = useCallback(async () => {
    if (auth.status !== "signed_in" || stateRef.current.allLabelsLoaded || labelsRef.current) return;
    labelsRef.current = true;
    try {
      const { rows } = await pageAll<{ labels: string[] | null }>((from, to) =>
        supabase.from("thoughts").select("labels").eq("owner_id", auth.user.id).range(from, to)
      );
      dispatch({ type: "SET_ALL_LABELS", labels: distinctLabels(rows) });
    } catch (err) {
      console.error("Loading labels failed:", err);
    } finally {
      labelsRef.current = false;
    }
  }, [auth.status, auth.user]);

  /**
   * Search the whole library server-side.
   *
   * Filtering the in-memory array only ever searched the pages that happened to
   * be loaded, so anyone past the first page was told "no results" for cards
   * sitting in their own library — the worst possible failure for a search box,
   * because it is indistinguishable from genuinely having nothing.
   *
   * Two queries, because `labels` is `text[]` and `ilike` cannot address it:
   * one substring match across the text columns, one exact-tag `overlaps` for
   * the labels whose names contain the query (resolved against the vocabulary
   * we already hold). Locally-known matches are folded in too, so a card
   * captured seconds ago is findable before the server has it.
   */
  const searchAllThoughts = useCallback(
    async (query: string): Promise<ThoughtSearchResult> => {
      const q = query.trim();
      if (q.length < 2) return { results: [], truncated: false, complete: true };

      const local = searchLoadedThoughts(q, stateRef.current.thoughts);
      if (auth.status !== "signed_in") return { results: local, truncated: false, complete: false };

      const ownerId = auth.user.id;
      const tags = labelsMatching(q, stateRef.current.allLabels);

      const select = () =>
        supabase
          .from("thoughts")
          .select(THOUGHT_COLUMNS)
          .eq("owner_id", ownerId)
          .order("created_at", { ascending: false })
          .order("id", { ascending: false })
          // One past the cap, purely to detect that there *is* more.
          .range(0, SEARCH_LIMIT);

      try {
        const responses = await Promise.all([
          select().or(textSearchFilter(q)),
          tags.length > 0
            ? select().overlaps("labels", tags)
            : Promise.resolve({ data: [] as unknown[], error: null }),
        ]);
        for (const res of responses) {
          if (res.error) throw res.error;
        }

        const rows = dedupeById(
          responses.flatMap((res) => (res.data ?? []) as unknown as ThoughtRow[])
        );
        const ids = rows.map((r) => r.id);
        const [connCounts, inThread] = await Promise.all([
          fetchConnectionCounts(ownerId, ids),
          fetchInThreadIds(ownerId, ids),
        ]);

        const server = rows.map((r) => ({
          ...rowToThought(r, connCounts.get(r.id) ?? 0),
          in_thread: inThread.has(r.id),
        }));

        // Local first, so an optimistic card's in-memory state wins over a
        // staler server copy of the same row.
        const merged = dedupeById([...local, ...server]).sort(byNewest);
        return {
          results: merged.slice(0, SEARCH_LIMIT),
          truncated: merged.length > SEARCH_LIMIT,
          complete: true,
        };
      } catch (err) {
        console.error("Search failed:", err);
        // Degrade to what's in memory rather than rendering a bare "no results",
        // which would be indistinguishable from an empty library.
        return { results: local, truncated: false, complete: false };
      }
    },
    [auth.status, auth.user, fetchConnectionCounts, fetchInThreadIds]
  );

  const deleteThought = useCallback(
    async (id: string) => {
      dispatch({ type: "DELETE_THOUGHT", id });
      if (auth.status === "signed_in" && !id.startsWith("temp-")) {
        // Cascade: clean up related records first
        await Promise.all([
          supabase.from("collection_thoughts").delete().eq("thought_id", id).eq("owner_id", auth.user.id),
          supabase.from("connections").delete().or(`from_thought_id.eq.${id},to_thought_id.eq.${id}`).eq("owner_id", auth.user.id),
          supabase.from("thread_thoughts").delete().eq("thought_id", id).eq("owner_id", auth.user.id),
          supabase.from("edition_thoughts").delete().eq("thought_id", id),
          supabase.from("thread_edition_thoughts").delete().eq("thought_id", id),
        ]);
        const { error } = await supabase.from("thoughts").delete().eq("id", id).eq("owner_id", auth.user.id);
        if (error) { showToast("Failed to delete thought."); return; }
      }
      showToast("Thought released.");
    },
    [auth.status, auth.user, showToast]
  );

  const withdrawFromCommons = useCallback(
    async (id: string, opts?: { silent?: boolean }): Promise<string | null> => {
      if (auth.status !== "signed_in" || id.startsWith("temp-")) return null;
      const { data, error } = await supabase.rpc("withdraw_from_commons", { p_thought_id: id });
      if (error || typeof data !== "string") {
        if (!opts?.silent) showToast("Couldn't withdraw the card — give it another go?");
        return null;
      }
      const newId = data;
      // Same id-swap pattern as the optimistic-insert flow: the card stays in
      // place locally, now pointing at the fresh private row. Collection
      // membership state refreshes on next hydration (the RPC already remapped
      // the private ones server-side).
      const { data: row } = await supabase
        .from("thoughts")
        .select("id,labels,created_at,updated_at,visibility")
        .eq("id", newId)
        .single();
      dispatch({
        type: "UPDATE_THOUGHT",
        id,
        updates: {
          id: newId,
          visibility: "private",
          labels: (row?.labels as string[] | null) ?? undefined,
          created_at: row?.created_at,
        },
      });
      track("card_withdrawn_from_commons", { card_id: id, new_card_id: newId });
      if (!opts?.silent) showToast("Card withdrawn — it's back in your library as a private card.");
      return newId;
    },
    [auth.status, auth.user, showToast]
  );

  const addTag = useCallback((thoughtId: string, tag: string) => {
    dispatch({ type: "ADD_TAG", thoughtId, tag });
    const thought = state.thoughts.find((t) => t.id === thoughtId);
    if (auth.status === "signed_in" && thought && !thoughtId.startsWith("temp-")) {
      const labels = Array.from(new Set([...(thought.labels ?? []), tag]));
      supabase.from("thoughts").update({ labels }).eq("id", thoughtId).eq("owner_id", auth.user.id)
        .then(({ error }) => { if (error) showToast("Failed to add label."); });
    }
  }, [auth.status, auth.user, state.thoughts, showToast]);

  const removeTag = useCallback((thoughtId: string, tag: string) => {
    dispatch({ type: "REMOVE_TAG", thoughtId, tag });
    const thought = state.thoughts.find((t) => t.id === thoughtId);
    if (auth.status === "signed_in" && thought && !thoughtId.startsWith("temp-")) {
      const labels = (thought.labels ?? []).filter((t) => t !== tag);
      supabase.from("thoughts").update({ labels }).eq("id", thoughtId).eq("owner_id", auth.user.id)
        .then(({ error }) => { if (error) showToast("Failed to remove label."); });
    }
  }, [auth.status, auth.user, state.thoughts, showToast]);

  const addToCollection = useCallback(
    (thoughtId: string, collectionId: string) => {
      dispatch({ type: "ADD_TO_COLLECTION", thoughtId, collectionId });
      const col = state.collections.find((c) => c.id === collectionId);
      const label = col ? col.name.trim().toLowerCase() : null;
      const thought = state.thoughts.find((t) => t.id === thoughtId);

      // Optimistically show the collection-name label right away.
      if (label && thought && !(thought.labels ?? []).includes(label)) {
        dispatch({ type: "UPDATE_THOUGHT", id: thoughtId, updates: { labels: [...(thought.labels ?? []), label] } });
      }

      if (auth.status === "signed_in" && !thoughtId.startsWith("temp-")) {
        const uid = auth.user.id;
        void (async () => {
          // 1. Membership first. Adding to a PUBLIC collection re-attributes the
          //    card server-side, which restores any labels stashed while it was
          //    anonymized — so this must land before we touch labels, or the
          //    write below would race with (and clobber) that restore.
          const position = col ? col.card_ids.length : 0;
          const { error: memberErr } = await supabase.from("collection_thoughts").upsert({
            owner_id: uid, collection_id: collectionId, thought_id: thoughtId, position,
          });
          if (memberErr) { showToast("Failed to add to collection."); return; }

          if (col?.is_public) {
            // Read the server's post-restore labels, merge in the collection
            // name, and persist + show the union — so any un-hidden labels come
            // back live instead of only after a refresh.
            const { data: row, error: readErr } = await supabase
              .from("thoughts").select("labels").eq("id", thoughtId).single();
            if (!readErr && row) {
              const serverLabels = (row.labels as string[] | null) ?? [];
              const merged = Array.from(new Set(label ? [...serverLabels, label] : serverLabels));
              if (label && !serverLabels.includes(label)) {
                await supabase.from("thoughts").update({ labels: merged }).eq("id", thoughtId).eq("owner_id", uid);
              }
              dispatch({ type: "UPDATE_THOUGHT", id: thoughtId, updates: { labels: merged } });
            }
          } else if (label && thought && !(thought.labels ?? []).includes(label)) {
            // Private collection — no server restore; just persist the label add.
            const labels = [...(thought.labels ?? []), label];
            await supabase.from("thoughts").update({ labels }).eq("id", thoughtId).eq("owner_id", uid);
          }
        })();
      }
      showToast("Added to collection.");
    },
    [auth.status, auth.user, showToast, state.collections, state.thoughts]
  );

  const removeFromCollection = useCallback(
    (thoughtId: string, collectionId: string) => {
      dispatch({ type: "REMOVE_FROM_COLLECTION", thoughtId, collectionId });
      if (auth.status === "signed_in" && !thoughtId.startsWith("temp-")) {
        supabase
          .from("collection_thoughts")
          .delete()
          .eq("collection_id", collectionId)
          .eq("thought_id", thoughtId)
          .eq("owner_id", auth.user.id)
          .then(({ error }) => { if (error) showToast("Failed to remove from collection."); });
      }
    },
    [auth.status, auth.user, showToast]
  );

  const createCollection = useCallback(
    async (name: string, description?: string, isPublic?: boolean, coverColor?: string): Promise<CollectionFull> => {
      const defaultColors = ["#D4848B", "#C9A96E", "#8A7668", "#A67C6D", "#4A1525"];
      const now = new Date().toISOString();
      const collection: CollectionFull = {
        id: `temp-col-${Date.now()}`,
        name,
        description,
        card_count: 0,
        is_public: isPublic || false,
        cover_color: coverColor || defaultColors[Math.floor(Math.random() * defaultColors.length)],
        updated_at: now,
        created_at: now,
        card_ids: [],
      };
      dispatch({ type: "CREATE_COLLECTION", collection });

      if (auth.status === "signed_in") {
        const { data } = await supabase
          .from("collections")
          .insert({
            owner_id: auth.user.id,
            name,
            description: description ?? null,
            is_public: isPublic ?? false,
            cover_color: collection.cover_color ?? null,
          })
          .select("id,created_at,updated_at")
          .single();
        if (data) {
          dispatch({
            type: "UPDATE_COLLECTION",
            id: collection.id,
            updates: { id: data.id, created_at: data.created_at, updated_at: data.updated_at },
          });
          track("collection_created", {
            collection_id: data.id,
            is_public: isPublic ?? false,
            has_description: !!description,
          });
          // Created public → mint the stable public_slug (empty collection, so
          // no cards are exposed yet; the RPC just allocates the link).
          let publicSlug: string | undefined;
          if (isPublic) {
            const { data: slug } = await supabase.rpc("set_collection_public", {
              p_collection_id: data.id,
              p_is_public: true,
            });
            if (typeof slug === "string") {
              publicSlug = slug;
              dispatch({ type: "UPDATE_COLLECTION", id: data.id, updates: { public_slug: slug } });
            }
          }
          return { ...collection, id: data.id, created_at: data.created_at, updated_at: data.updated_at, public_slug: publicSlug };
        }
      }
      showToast("Collection created.");
      return collection;
    },
    [auth.status, auth.user, showToast]
  );

  const updateCollection = useCallback(
    (id: string, updates: Partial<import("./CollectionCard").Collection>) => {
      dispatch({ type: "UPDATE_COLLECTION", id, updates });
      if (auth.status === "signed_in" && !id.startsWith("temp-col-")) {
        supabase.from("collections").update(updates).eq("id", id).eq("owner_id", auth.user.id)
          .then(({ error }) => { if (error) showToast("Failed to update collection."); });
      }
    },
    [auth.status, auth.user, showToast]
  );

  const setCollectionPublic = useCallback(
    async (id: string, isPublic: boolean): Promise<string | null> => {
      // Optimistic: flip the flag locally so the UI reacts immediately.
      dispatch({ type: "UPDATE_COLLECTION", id, updates: { is_public: isPublic } });
      if (auth.status !== "signed_in" || id.startsWith("temp-col-")) return null;

      const { data, error } = await supabase.rpc("set_collection_public", {
        p_collection_id: id,
        p_is_public: isPublic,
      });
      if (error) {
        // Roll back the optimistic flip.
        dispatch({ type: "UPDATE_COLLECTION", id, updates: { is_public: !isPublic } });
        showToast(isPublic ? "Failed to make collection public." : "Failed to make collection private.");
        return null;
      }
      const slug = typeof data === "string" ? data : undefined;
      if (slug) dispatch({ type: "UPDATE_COLLECTION", id, updates: { public_slug: slug } });

      // The visibility flip recomputes every member card server-side —
      // restoring stashed labels when going public, or hiding them when going
      // private. Pull the fresh labels so the change shows live, not only after
      // a refresh. (A card in another public source keeps its labels either way.)
      {
        const col = state.collections.find((c) => c.id === id);
        const cardIds = (col?.card_ids ?? []).filter((cid) => !cid.startsWith("temp-"));
        if (cardIds.length > 0) {
          const { data: rows } = await supabase.from("thoughts").select("id,labels").in("id", cardIds);
          rows?.forEach((r) =>
            dispatch({ type: "UPDATE_THOUGHT", id: r.id as string, updates: { labels: (r.labels as string[] | null) ?? [] } })
          );
        }
      }

      // Promote the (private) cover into the public bucket so the /c/ page can
      // show it. Best-effort: a failed promotion just falls back to the color.
      if (isPublic) {
        const col = state.collections.find((c) => c.id === id);
        if (col?.cover_image_path) {
          const publicUrl = await promoteCollectionCoverToPublic(col.cover_image_path, auth.user.id);
          if (publicUrl) {
            await supabase.from("collections").update({ cover_image_public_url: publicUrl }).eq("id", id).eq("owner_id", auth.user.id);
            dispatch({ type: "UPDATE_COLLECTION", id, updates: { cover_image_public_url: publicUrl } });
          }
        }
      }

      track("collection_visibility_changed", {
        collection_id: id,
        is_public: isPublic,
        card_count: state.collections.find((c) => c.id === id)?.card_count,
      });
      showToast(
        isPublic
          ? "Collection is public — anyone with the link can view it."
          : "Collection is private. Cards you'd shared are unlinked from you — they stay anonymous in the commons and can be re-linked by making it public again."
      );
      return slug ?? null;
    },
    [auth.status, auth.user, state.collections, showToast]
  );

  const deleteCollection = useCallback(
    async (id: string) => {
      dispatch({ type: "DELETE_COLLECTION", id });
      if (auth.status === "signed_in" && !id.startsWith("temp-col-")) {
        // Cascade: clean up collection_thoughts, editions, and edition_thoughts first
        const { data: editions } = await supabase.from("editions").select("id").eq("collection_id", id).eq("owner_id", auth.user.id);
        if (editions && editions.length > 0) {
          const edIds = editions.map((e) => e.id);
          await Promise.all([
            supabase.from("edition_thoughts").delete().in("edition_id", edIds),
            supabase.from("edition_events").delete().in("edition_id", edIds).eq("owner_id", auth.user.id),
          ]);
          await supabase.from("editions").delete().eq("collection_id", id).eq("owner_id", auth.user.id);
        }
        await supabase.from("collection_thoughts").delete().eq("collection_id", id).eq("owner_id", auth.user.id);
        const { error } = await supabase.from("collections").delete().eq("id", id).eq("owner_id", auth.user.id);
        if (error) { showToast("Failed to delete collection."); return; }
      }
      showToast("Collection removed.");
    },
    [auth.status, auth.user, showToast]
  );

  const bulkAddToCollection = useCallback(
    (thoughtIds: string[], collectionId: string) => {
      dispatch({ type: "BULK_ADD_TO_COLLECTION", thoughtIds, collectionId });
      const col = state.collections.find((c) => c.id === collectionId);
      const label = col ? col.name.trim().toLowerCase() : null;

      // Optimistically show the collection-name label on each card.
      if (label) {
        thoughtIds.forEach((tid) => {
          const thought = state.thoughts.find((t) => t.id === tid);
          if (thought && !(thought.labels ?? []).includes(label)) {
            dispatch({ type: "UPDATE_THOUGHT", id: tid, updates: { labels: [...(thought.labels ?? []), label] } });
          }
        });
      }

      if (auth.status === "signed_in") {
        const uid = auth.user.id;
        const persistIds = thoughtIds.filter((id) => !id.startsWith("temp-"));
        void (async () => {
          // 1. Membership first — re-attributing to a PUBLIC collection restores
          //    any labels stashed while a card was anonymized, so this must land
          //    before we touch labels (see addToCollection for the full note).
          const base = col ? col.card_ids.length : 0;
          const rows = persistIds.map((id, idx) => ({
            owner_id: uid, collection_id: collectionId, thought_id: id, position: base + idx,
          }));
          if (rows.length > 0) {
            const { error } = await supabase.from("collection_thoughts").upsert(rows);
            if (error) { showToast("Failed to sort thoughts."); return; }
          }

          if (col?.is_public && persistIds.length > 0) {
            // Reconcile each card's labels with the server's post-restore state
            // so any un-hidden labels show live, not just after a refresh.
            const { data, error: readErr } = await supabase
              .from("thoughts").select("id,labels").in("id", persistIds);
            if (!readErr && data) {
              await Promise.all(data.map(async (r) => {
                const serverLabels = (r.labels as string[] | null) ?? [];
                const merged = Array.from(new Set(label ? [...serverLabels, label] : serverLabels));
                if (label && !serverLabels.includes(label)) {
                  await supabase.from("thoughts").update({ labels: merged }).eq("id", r.id).eq("owner_id", uid);
                }
                dispatch({ type: "UPDATE_THOUGHT", id: r.id as string, updates: { labels: merged } });
              }));
            }
          } else if (label) {
            // Private collection — persist the optimistic label adds.
            await Promise.all(persistIds.map(async (tid) => {
              const thought = state.thoughts.find((t) => t.id === tid);
              if (thought && !(thought.labels ?? []).includes(label)) {
                const labels = [...(thought.labels ?? []), label];
                await supabase.from("thoughts").update({ labels }).eq("id", tid).eq("owner_id", uid);
              }
            }));
          }
        })();
      }
      showToast(`${thoughtIds.length} thoughts sorted.`);
    },
    [auth.status, auth.user, showToast, state.collections, state.thoughts]
  );

  const createEdition = useCallback(
    async (collectionId: string, name: string, customSlug?: string, password?: string, viewModes?: ("reader" | "map")[], mapSnapshot?: MapSnapshot | null, coverImageUrl?: string): Promise<Edition> => {
      const modes = viewModes ?? ["reader"];
      const edition: Edition = {
        id: `temp-ed-${Date.now()}`,
        collection_id: collectionId,
        name,
        created_at: new Date().toISOString(),
        thought_ids: state.collections.find((c) => c.id === collectionId)?.card_ids ?? [],
        status: "draft",
        view_modes: modes,
        map_snapshot: mapSnapshot ?? null,
        cover_image_url: coverImageUrl,
      };

      dispatch({ type: "CREATE_EDITION", edition });

      if (auth.status === "signed_in") {
        const { data: { session } } = await supabase.auth.refreshSession();
        if (!session) { showToast("Session expired. Please sign in again."); return edition; }

        const { data, error } = await supabase.functions
          .invoke("publish_edition", {
            body: {
              collection_id: collectionId,
              name,
              custom_slug: customSlug || undefined,
              password: password || undefined,
              view_modes: modes,
              map_snapshot: mapSnapshot ?? undefined,
            },
            headers: { Authorization: `Bearer ${session.access_token}` },
          });

        if (error || !data) {
          // The function says something worth reading — most often that the
          // custom link is taken, which is fixable in five seconds if you're
          // told. supabase-js buries that body inside the failed Response, so
          // "Failed to publish edition." was all anyone ever saw.
          const detail = await readFunctionError(error);
          showToast(detail ?? "Failed to publish edition.");
          // Drop the optimistic edition instead of leaving it in the list as a
          // draft. It still carries its `temp-ed-` id, and every action on that
          // row — settings, history, cover, delete — sends the id to PostgREST,
          // which rejects it as an invalid uuid. A publish that failed should
          // leave nothing behind to click on.
          dispatch({ type: "DELETE_EDITION", id: edition.id });
          track("edition_publish_failed", {
            collection_id: collectionId,
            reason: detail ?? error?.message ?? "unknown",
          });
          return { ...edition, status: "draft" };
        }

        const updates = {
          db_id: data.edition_id,
          id: data.public_slug ?? data.edition_id,
          public_slug: data.public_slug,
          status: "published" as const,
        };
        dispatch({ type: "UPDATE_EDITION", id: edition.id, updates });

        // Publishing an edition re-attributes its cards server-side (the
        // publish_edition function marks them public and adds edition_thoughts),
        // which un-hides any labels stashed while they were anonymized. Refetch
        // so those labels show live instead of only after a refresh.
        {
          const edThoughtIds = edition.thought_ids.filter((tid) => !tid.startsWith("temp-"));
          if (edThoughtIds.length > 0) {
            const { data: rows } = await supabase.from("thoughts").select("id,labels").in("id", edThoughtIds);
            rows?.forEach((r) =>
              dispatch({ type: "UPDATE_THOUGHT", id: r.id as string, updates: { labels: (r.labels as string[] | null) ?? [] } })
            );
          }
        }

        // Cover is public; persist it on the edition row (owner-update RLS).
        if (coverImageUrl && data.edition_id) {
          await supabase
            .from("editions")
            .update({ cover_image_url: coverImageUrl })
            .eq("id", data.edition_id)
            .eq("owner_id", auth.user.id);
        }
        track("edition_created", {
          edition_id: data.edition_id,
          collection_id: collectionId,
          view_modes: modes,
          mode_count: modes.length,
          has_password: !!password,
          has_cover: !!coverImageUrl,
          thought_count: edition.thought_ids.length,
        });
        showToast("Edition published.");
        return { ...edition, ...updates };
      }

      return edition;
    },
    [auth.status, auth.user, state.collections, showToast]
  );

  const saveThreadDraft = useCallback(
    async (threadId: string | null, title: string, thoughtIds: string[]): Promise<string> => {
      if (auth.status !== "signed_in") throw new Error("You must be signed in.");
      if (!title.trim()) throw new Error("Trail title is required.");

      const userId = auth.user.id;
      const now = new Date().toISOString();

      let id = threadId;

      if (!id) {
        const { data: inserted, error: insErr } = await supabase
          .from("threads")
          .insert({ owner_id: userId, title: title.trim() })
          .select("id")
          .single();

        if (insErr || !inserted) throw new Error("Failed to create trail.");
        id = inserted.id as string;
      } else {
        const { error: updErr } = await supabase
          .from("threads")
          .update({ title: title.trim(), updated_at: now })
          .eq("id", id)
          .eq("owner_id", userId);

        if (updErr) throw new Error("Failed to update trail.");
      }

      // Replace ordered membership
      const { error: delErr } = await supabase.from("thread_thoughts").delete().eq("thread_id", id);
      if (delErr) throw new Error("Failed to clear existing trail steps.");

      if (thoughtIds.length > 0) {
        const rows = thoughtIds.map((thought_id, idx) => ({
          thread_id: id as string,
          thought_id,
          owner_id: userId,
          position: idx,
        }));

        const { error: insErr } = await supabase.from("thread_thoughts").insert(rows);
        if (insErr) throw new Error("Failed to save trail steps.");

        // Create chain connections between consecutive thread cards
        if (thoughtIds.length >= 2) {
          // Load existing connections to avoid duplicates
          const { data: existing } = await supabase
            .from("connections")
            .select("from_thought_id,to_thought_id")
            .eq("owner_id", userId);

          const existingSet = new Set(
            (existing ?? []).map((c) => [c.from_thought_id, c.to_thought_id].sort().join(":"))
          );

          const newConns = [];
          for (let i = 0; i < thoughtIds.length - 1; i++) {
            const pair = [thoughtIds[i], thoughtIds[i + 1]].sort().join(":");
            if (!existingSet.has(pair)) {
              newConns.push({
                owner_id: userId,
                from_thought_id: thoughtIds[i],
                to_thought_id: thoughtIds[i + 1],
                note: `Trail: ${title.trim()}`,
              });
              existingSet.add(pair); // Prevent duplicates within this batch
            }
          }

          if (newConns.length > 0) {
            await supabase.from("connections").insert(newConns);
          }
        }
      }

      // Update saved threads list — keep any existing cover on edit.
      const existingCover = state.savedThreads.find((t) => t.id === id)?.cover_image_path;
      const savedThread: Thread = { id: id as string, title: title.trim(), created_at: now, updated_at: now, cover_image_path: existingCover };
      dispatch({ type: "SET_SAVED_THREADS", threads: [savedThread, ...state.savedThreads.filter((t) => t.id !== id)] });

      track(threadId ? "thread_updated" : "thread_created", {
        thread_id: id as string,
        step_count: thoughtIds.length,
      });

      return id as string;
    },
    [auth.status, auth.user, state.savedThreads]
  );

  const setThreadCover = useCallback(
    (threadId: string, path: string | undefined) => {
      dispatch({
        type: "SET_SAVED_THREADS",
        threads: state.savedThreads.map((t) =>
          t.id === threadId ? { ...t, cover_image_path: path } : t
        ),
      });
      if (auth.status === "signed_in") {
        supabase
          .from("threads")
          .update({ cover_image_path: path ?? null })
          .eq("id", threadId)
          .eq("owner_id", auth.user.id)
          .then(({ error }) => { if (error) showToast("Failed to update cover."); });
      }
    },
    [auth.status, auth.user, state.savedThreads, showToast]
  );

  const deleteThread = useCallback(
    async (threadId: string) => {
      dispatch({ type: "SET_SAVED_THREADS", threads: state.savedThreads.filter((t) => t.id !== threadId) });
      if (auth.status === "signed_in") {
        await supabase.from("thread_thoughts").delete().eq("thread_id", threadId);
        const { error } = await supabase.from("threads").delete().eq("id", threadId).eq("owner_id", auth.user.id);
        if (error) { showToast("Failed to delete trail."); return; }
      }
      showToast("Trail deleted.");
    },
    [auth.status, auth.user, state.savedThreads, showToast]
  );

  const publishThreadEdition = useCallback(
    async (threadId: string, name?: string) => {
      if (auth.status !== "signed_in") throw new Error("You must be signed in.");

      const { data: { session: tsSession } } = await supabase.auth.refreshSession();
      if (!tsSession) throw new Error("Session expired.");

      const { data, error } = await supabase.functions.invoke("publish_thread_edition", {
        body: { thread_id: threadId, name: name?.trim() || undefined },
        headers: { Authorization: `Bearer ${tsSession.access_token}` },
      });

      if (error || !data) throw new Error("Failed to publish trail.");
      const res = data as { public_slug: string; edition_id: string };
      return res;
    },
    [auth.status, auth.user]
  );

  const reorderCollection = useCallback((collectionId: string, cardIds: string[]) => {
    dispatch({ type: "REORDER_COLLECTION", collectionId, cardIds });
    if (auth.status === "signed_in") {
      const rows = cardIds
        .filter((id) => !id.startsWith("temp-"))
        .map((thought_id, idx) => ({
          owner_id: auth.user.id,
          collection_id: collectionId,
          thought_id,
          position: idx,
        }));
      if (rows.length > 0) {
        supabase.from("collection_thoughts").upsert(rows)
          .then(({ error }) => { if (error) showToast("Failed to save card order."); });
      }
    }
  }, [auth.status, auth.user, showToast]);

  const getCollectionThoughts = useCallback(
    (collectionId: string): Thought[] => {
      const collection = state.collections.find((c) => c.id === collectionId);
      if (!collection) return [];
      return collection.card_ids
        .map((id) => state.thoughts.find((t) => t.id === id))
        .filter(Boolean) as Thought[];
    },
    [state.collections, state.thoughts]
  );

  const getThoughtCollections = useCallback(
    (thoughtId: string): CollectionFull[] => {
      return state.collections.filter((c) => c.card_ids.includes(thoughtId));
    },
    [state.collections]
  );

  /** Instant, in-memory matches. Complete only when `state.thoughtsAllLoaded`;
   *  callers that must not miss anything use `searchAllThoughts`. */
  const searchThoughts = useCallback(
    (query: string): Thought[] => searchLoadedThoughts(query, state.thoughts),
    [state.thoughts]
  );

  const setActiveThread = useCallback((thread: string[]) => {
    dispatch({ type: "SET_ACTIVE_THREAD", thread });
  }, []);

  // A borrow is written straight to Postgres by socialApi, bypassing this store.
  // Hydration only re-runs on auth changes and loadMoreThoughts pages *backwards*
  // from a created_at cursor, so nothing could ever surface the new card — it
  // stayed invisible until a full reload. The row already exists server-side by
  // the time this is called, so this is a state sync, not an optimistic write.
  const registerBorrowedThought = useCallback((card: BorrowedCardSeed) => {
    const thought: Thought = {
      id: card.id,
      title: card.title ?? undefined,
      body: card.body,
      source_url: card.source_url ?? undefined,
      source_title: card.source_title ?? undefined,
      labels: card.labels ?? [],
      connections: 0,
      created_at: card.created_at,
      author: "You",
      visibility: "private",
      source_card_id: card.source_card_id ?? undefined,
      source_edition_id: card.source_edition_id ?? undefined,
    };
    dispatch({ type: "ADD_THOUGHT", thought });
  }, []);

  const value: BurrowContextValue = {
    state,
    dispatch,
    addThought,
    addImageThought,
    updateThought,
    deleteThought,
    withdrawFromCommons,
    loadMoreThoughts,
    loadAllThoughts,
    loadAllLabels,
    ensureThoughtsLoaded,
    searchAllThoughts,
    addTag,
    removeTag,
    addToCollection,
    removeFromCollection,
    createCollection,
    updateCollection,
    setCollectionPublic,
    deleteCollection,
    bulkAddToCollection,
    createEdition,
    reorderCollection,
    saveThreadDraft,
    setThreadCover,
    deleteThread,
    publishThreadEdition,
    showToast,
    registerBorrowedThought,
    getCollectionThoughts,
    getThoughtCollections,
    searchThoughts,
    setActiveThread,
  };

  return <BurrowContext.Provider value={value}>{children}</BurrowContext.Provider>;
}